import { Button, Typography } from "@cur8/maneki";
import { useMutation } from "@tanstack/react-query";
import { useAssignToEcgMutation } from "hooks/mutations/useAssignToEcgMutation";
import { useECGDeviceQuery } from "hooks/queries/useECGDeviceQuery";
import { paths } from "render/routes/paths";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import styles from "./styles.module.sass";
import { ReactNode, useMemo } from "react";
import { APITypesV1 } from "@cur8/api-client";
import { usePatientQuery } from "hooks/queries/usePatientQuery";

interface State {
  label: ReactNode;
  subLabel: ReactNode;
  cta: {
    submit?: ReactNode;
    cancel?: ReactNode;
  };
}

export function AssignToNoravECGDevice({
  patientId,
  visitId,
  goTo,
}: {
  patientId: string;
  visitId: string;
  goTo(url: string): void;
}) {
  const patientQuery = usePatientQuery({ patientId });
  const eCGDeviceQuery = useECGDeviceQuery({ patientId, visitId });
  const assignToEcgMutation = useAssignToEcgMutation();
  const submitHandler = useMutation({
    mutationFn: () => assignToEcgMutation.mutateAsync({ patientId, visitId }),
    onSuccess: async () => {
      await eCGDeviceQuery.refetch();
      goTo(paths.checkInSuccess.url({ patientId, visitId }));
    },
  });

  const state = useMemo<State | undefined>(() => {
    if (submitHandler.isError) {
      return {
        label: "Couldn’t assign member to the ECG",
        subLabel:
          "Please try again, or cancel and proceed with the other examinations.",
        cta: {
          submit: "Try again",
          cancel: "Cancel",
        },
      };
    }
    if (eCGDeviceQuery.error) {
      return {
        label: "Couldn’t connect to ECG device",
        subLabel: "Please check the device, or contact support",
        cta: {
          cancel: "Cancel",
        },
      };
    }
    switch (eCGDeviceQuery.data?.assignmentStatus) {
      case APITypesV1.DeviceAssignmentStatus.AssignedToMember:
        return {
          label: `Are you sure you want to re-assign ${patientQuery.data?.name?.displayName} to the ECG device?`,
          subLabel: "A recording is currently in progress.",
          cta: {
            submit: "Re-assign to ECG",
            cancel: "Cancel",
          },
        };
      case APITypesV1.DeviceAssignmentStatus.CanAssign:
      case APITypesV1.DeviceAssignmentStatus.OtherMemberAssigned:
        return {
          label: `Assign ${patientQuery.data?.name?.displayName} to the ECG device?`,
          subLabel: "This will assign member to Norav ECG device in the room",
          cta: {
            submit: "Assign to ECG",
            cancel: "Cancel",
          },
        };
    }
    return undefined;
  }, [
    submitHandler.isError,
    eCGDeviceQuery.data,
    eCGDeviceQuery.error,
    patientQuery.data,
  ]);

  if (state == null) {
    return;
  }

  return (
    <PageFrameContent>
      <div className={styles.AssignToNoravECGDevice}>
        <section className={styles.title}>
          <Typography variant="display-s">{state?.label}</Typography>
          <Typography variant="body-m">{state?.subLabel}</Typography>
        </section>
        <section></section>
        <section className={styles.cta}>
          {state?.cta.cancel ? (
            <Button
              fillContainer
              variant="secondary"
              onClick={() =>
                goTo(paths.checkInSuccess.url({ patientId, visitId }))
              }
            >
              {state?.cta.cancel}
            </Button>
          ) : undefined}
          {state?.cta.submit ? (
            <Button
              fillContainer
              state={
                submitHandler.isPending || submitHandler.isSuccess
                  ? "loading"
                  : undefined
              }
              onClick={() => submitHandler.mutate()}
            >
              {state?.cta.submit}
            </Button>
          ) : undefined}
        </section>
      </div>
    </PageFrameContent>
  );
}
